import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, ImagePlus, Loader2, ShieldCheck, User, Users, UsersRound, X } from "lucide-react";
import { api, WHATSAPP_BASE } from "../../lib/api";
import Reveal from "./Reveal";

const PRODUCTS = [
  { id: "single", name: "Single", people: "1 person", icon: User, prices: { "10": 1000, "20": 2000 } },
  { id: "couple", name: "Couple", people: "2 people", icon: Users, prices: { "10": 1700, "20": 3400 } },
  { id: "family", name: "Family", people: "3-4 people", icon: UsersRound, prices: { "10": 3000, "20": 6000 } },
];
const MAX_IMAGES = 5;
const MAX_IMG_BYTES = 10 * 1024 * 1024;

const TERMS = [
  "Made to order: every statue is custom-sculpted from your photos in PLA. Once an order is placed and paid, it cannot be cancelled or refunded.",
  "Handcrafted variance: 3D printing and hand-finishing are craft processes — the finished statue can differ from your photo by up to 30% in likeness, colour and surface detail.",
  "Confirmation call: we call you before printing to confirm photos and details. Printing starts only after your confirmation.",
  "Photos: clear, front-facing, well-lit photos give the best result. Blurry or dark photos may reduce likeness.",
  "Delivery: timeline and delivery or pickup details are confirmed on the call.",
];

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function OrderSection() {
  const [size, setSize] = useState("10");
  const [product, setProduct] = useState("couple");
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "" });
  const [images, setImages] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [orderId, setOrderId] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selected = PRODUCTS.find((p) => p.id === product);
  const amount = selected.prices[size];
  const busy = status === "creating" || status === "paying" || status === "verifying";

  const addImages = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => f.type.startsWith("image/") && f.size <= MAX_IMG_BYTES);
    setImages((prev) => [...prev, ...valid].slice(0, MAX_IMAGES));
    e.target.value = "";
  };

  const pay = async (e) => {
    e.preventDefault();
    setError("");
    const digits = form.phone.replace(/\D/g, "");
    if (!form.name.trim()) return setError("Please tell us your name.");
    if (digits.length < 8 || digits.length > 15) return setError("Please enter a valid phone number.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError("Please enter a valid email address — your confirmation goes there.");
    if (!form.address.trim()) return setError("Please add your delivery address.");
    if (images.length === 0) return setError("Please add at least one clear photo of the person(s).");
    if (!agreed) return setError("Please read and accept the Terms & Conditions first.");
    const loaded = await loadRazorpay();
    if (!loaded) return setError("The payment window failed to load. Check your connection and try again.");
    setStatus("creating");
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("email", form.email);
      fd.append("address", form.address);
      fd.append("product", product);
      fd.append("size_cm", size);
      images.forEach((f) => fd.append("images", f));
      const res = await api.post("/orders", fd);
      const d = res.data;
      setOrderId(d.order_id);
      setStatus("paying");
      const rzp = new window.Razorpay({
        key: d.key_id,
        amount: d.amount,
        currency: d.currency,
        order_id: d.razorpay_order_id,
        name: "Biswas3D",
        description: `${selected.name} statue · ${size} cm · PLA`,
        prefill: { name: form.name, contact: digits.slice(-10), email: form.email.trim() },
        theme: { color: "#2563eb" },
        handler: async (payRes) => {
          setStatus("verifying");
          try {
            await api.post("/orders/verify", {
              order_id: d.order_id,
              razorpay_order_id: payRes.razorpay_order_id,
              razorpay_payment_id: payRes.razorpay_payment_id,
              razorpay_signature: payRes.razorpay_signature,
            });
            setStatus("success");
          } catch {
            setError(`Payment received but confirmation failed. Message us on WhatsApp with payment ID ${payRes.razorpay_payment_id} — your order is safe.`);
            setStatus("idle");
          }
        },
        modal: { ondismiss: () => setStatus((s) => (s === "success" ? s : "idle")) },
      });
      rzp.on("payment.failed", (resp) => {
        setError(`Payment failed${resp.error?.description ? `: ${resp.error.description}` : ""}. You were not charged — please try again.`);
        setStatus("idle");
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not start the payment. Please try again.");
      setStatus("idle");
    }
  };

  const reset = () => { setForm({ name: "", phone: "", email: "", address: "" }); setImages([]); setAgreed(false); setStatus("idle"); setOrderId(""); };
  const waMessage = `${WHATSAPP_BASE}?text=${encodeURIComponent(`Hi Biswas3D, I'm ${form.name}. I just paid for a ${selected.name} statue (${size} cm). Order #${orderId.slice(0, 8)}.`)}`;

  return (
    <section className="order-section" id="order" data-testid="order-section">
      <div className="section-wrap order-wrap">
        <Reveal>
          {status === "success" ? (
            <div className="success-card" data-testid="order-success">
              <CheckCircle2 size={34} />
              <h3>Payment received.</h3>
              <span className="ref" data-testid="order-reference">Order #{orderId.slice(0, 8).toUpperCase()} &middot; {selected.name} &middot; {size} cm</span>
              <p>Thank you, {form.name.split(" ")[0]}. A confirmation email is on its way to {form.email}, and we will call you on {form.phone} within 24 hours to confirm photos and details before printing.</p>
              <div className="success-actions">
                <a className="button button-primary" data-testid="order-whatsapp-link" href={waMessage} target="_blank" rel="noreferrer"><span>Say hello on WhatsApp</span><ArrowUpRight size={16} /></a>
                <button className="text-button" data-testid="order-reset-button" onClick={reset}>Place another order</button>
              </div>
            </div>
          ) : (
            <form className="enquiry-form" data-testid="order-form" onSubmit={pay}>
              <div className="order-head">
                <p className="eyebrow">Secure checkout</p>
                <h2>Order your statue.</h2>
              </div>
              <div className="size-toggle" data-testid="size-toggle">
                <button type="button" className={size === "10" ? "active" : ""} data-testid="size-toggle-10" onClick={() => setSize("10")}>10 cm</button>
                <button type="button" className={size === "20" ? "active" : ""} data-testid="size-toggle-20" onClick={() => setSize("20")}>20 cm</button>
              </div>
              <div className="product-grid" data-testid="product-grid">
                {PRODUCTS.map((p) => {
                  const Icon = p.icon;
                  return (
                    <button type="button" key={p.id} className={`product-card ${product === p.id ? "active" : ""}`} data-testid={`product-card-${p.id}`} onClick={() => setProduct(p.id)}>
                      <Icon size={22} />
                      <span className="pc-name">{p.name}</span>
                      <span className="pc-people">{p.people}</span>
                      <span className="pc-price" data-testid={`price-${p.id}`}>&#8377;{p.prices[size].toLocaleString("en-IN")} <small>/ {size} cm</small></span>
                    </button>
                  );
                })}
              </div>
              <div className="field-row">
                <div className="field"><label htmlFor="od-name">Your name</label><input id="od-name" data-testid="order-name-input" value={form.name} onChange={set("name")} placeholder="Full name" /></div>
                <div className="field"><label htmlFor="od-phone">Phone number</label><input id="od-phone" data-testid="order-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." /></div>
              </div>
              <div className="field"><label htmlFor="od-email">Email address</label><input id="od-email" type="email" data-testid="order-email-input" value={form.email} onChange={set("email")} placeholder="Your order confirmation goes here" /></div>
              <div className="field"><label htmlFor="od-address">Delivery address</label><input id="od-address" data-testid="order-address-input" value={form.address} onChange={set("address")} placeholder="Where should the statue reach?" /></div>
              <div className="field">
                <label>Photos of the person(s) — up to {MAX_IMAGES}</label>
                <label className="file-drop" data-testid="order-images-drop">
                  <ImagePlus size={18} />
                  <span>{images.length ? `${images.length} photo${images.length > 1 ? "s" : ""} selected` : "Clear front-facing photos work best — JPG, PNG or WEBP"}</span>
                  <input type="file" accept="image/*" multiple hidden data-testid="order-images-input" onChange={addImages} />
                </label>
                {images.length > 0 && (
                  <div className="selected-files">
                    {images.map((f, i) => (
                      <span className="file-chip" key={`${f.name}-${i}`}>{f.name}<button type="button" data-testid={`order-image-remove-${i}`} onClick={() => setImages(images.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`}><X size={12} /></button></span>
                    ))}
                  </div>
                )}
              </div>
              <div className="terms-box" data-testid="terms-box">
                <details data-testid="terms-details">
                  <summary data-testid="terms-summary">Read Terms &amp; Conditions</summary>
                  <ul className="terms-list">
                    {TERMS.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                  <Link to="/terms" target="_blank" className="terms-full-link" data-testid="terms-full-link">Open the full Terms &amp; Conditions page</Link>
                </details>
                <label className="terms-check" data-testid="terms-check-label">
                  <input type="checkbox" data-testid="terms-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                  <span>I have read and agree to the Terms &amp; Conditions — I understand orders cannot be cancelled and up to 30% variation is normal in handcrafted prints.</span>
                </label>
              </div>
              {error && <p className="form-error" data-testid="order-error">{error}</p>}
              <button className="button button-primary pay-button" data-testid="pay-button" type="submit" disabled={busy || !agreed}>
                {busy ? <><Loader2 size={17} className="spin" /><span>{status === "creating" ? "Preparing payment..." : status === "paying" ? "Waiting for payment..." : "Confirming..."}</span></> : <><ShieldCheck size={17} /><span>Pay &#8377;{amount.toLocaleString("en-IN")} securely</span></>}
              </button>
              <p className="form-hint">Secure checkout by Razorpay — UPI, cards &amp; netbanking accepted.</p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
